// Логика игрового мира без React: что рисуем (по порядку сервера), полоса опыта, таймеры, тексты ошибок действий.
import { ApiError } from '../../api/client';
import type { AppConfig } from '../../api/types';
import { gameOff } from '../../gameSwitch';
import type { FriendLeagueRowLike, WorldDot, WorldBanner, WorldHint, WorldHubTab, WorldLevelLock, WorldOpens, WorldPanelNext, WorldPanelWallet, WorldTopTabs, WorldScene, WorldSceneButton, WorldDivision, WorldDivisionGroup, WorldPlayer, WorldTab, WorldTabBar, WorldTeamChoice } from '../../api/worldTypes';
import type { StringKey } from '../../i18n/i18n';

/** Разделы мира, которые умеем рисовать. Незнакомый ключ (будущая «Лавка» — shop) молча пропускаем. */
// club — навигация v3 (§273): «Бой · Матчи · Клуб»
export const WORLD_TAB_KEYS = ['player', 'matches', 'rating', 'guild', 'club'];
/** Блоки главной «Мой игрок». daily / training / inventory сняты сервером (§278) — приедут в старом ответе, пропускаем.
 *  login намеренно: вход — кнопка сцены и папка Награды. Сервер кладёт login в blocks_order (§299, ответ меняется только
 *  добавлением — §347), но блоком на главной его не рисуем: календарь входа открывается подэкраном login (HQ-780).
 *  cards снят вместе с карточками (В2, HQ-782): старый ответ его ещё несёт — мимо. */
export const WORLD_BLOCK_KEYS = ['player', 'chests', 'tasks', 'community'];

/** Адрес мира из конфига: есть и мир не выключен флагом — «Игра» ведёт в мир; иначе null (прежний экран).
 *  Выключена вся игра (gameOff, HQ-1572) — мира тоже нет: энергия за статьи и ролики молчит. */
export function worldPathOf(config: AppConfig | undefined): string | null {
  if (gameOff(config) || config?.features?.game_world === false) return null;
  return config?.game?.world_path || null;
}

/** Разделы панели мира строго в порядке tabs_order; без описания или незнакомый — мимо. */
export function visibleTabs(bar: WorldTabBar | undefined): WorldTab[] {
  if (!bar) return [];
  return bar.tabs_order.flatMap((key) => {
    const tab = bar.tabs?.[key];
    return tab && WORLD_TAB_KEYS.includes(key) && tab.endpoint ? [{ ...tab, key }] : [];
  });
}

/** Активный раздел: из адреса → default сервера → первый. */
export function activeTab(tabs: WorldTab[], wanted: string | undefined, fallback: string | undefined): WorldTab | undefined {
  return tabs.find((t) => t.key === wanted) ?? tabs.find((t) => t.key === fallback) ?? tabs[0];
}

/** Блоки главной по blocks_order: только знакомые и приехавшие (пустой блок не приезжает). */
export function visibleBlocks(order: string[], data: Record<string, unknown>): string[] {
  return order.filter((key) => WORLD_BLOCK_KEYS.includes(key) && data[key] != null);
}

/** Полоса опыта: доля пути от xp_level_start до xp_next_level (0…1); последний уровень (null) — полная. */
export function xpProgress(p: Pick<WorldPlayer, 'xp' | 'xp_level_start' | 'xp_next_level'>): number {
  if (p.xp_next_level === null) return 1;
  const span = p.xp_next_level - p.xp_level_start;
  if (span <= 0) return 1;
  return Math.min(1, Math.max(0, (p.xp - p.xp_level_start) / span));
}

/** Остаток времени «Ч:ММ:СС» (цифры не переводятся); прошло — null. */
export function countdown(until: string | null | undefined, now: number): string | null {
  if (!until) return null;
  const left = Math.floor((Date.parse(until) - now) / 1000);
  if (!Number.isFinite(left) || left <= 0) return null;
  const h = Math.floor(left / 3600);
  const m = Math.floor((left % 3600) / 60);
  const s = left % 60;
  return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/** Короткий таймер: меньше часа — «М:СС», иначе как countdown. */
export function shortCountdown(until: string | null | undefined, now: number): string | null {
  const full = countdown(until, now);
  return full?.startsWith('0:') ? full.slice(2).replace(/^0(\d)/, '$1') : full;
}

const REASONS: Record<string, StringKey> = {
  chest_expired: 'tg_world_err_chest_expired',
  task_not_done: 'tg_world_err_task_not_done',
  // трофейная дорога
  road_tier_locked: 'tg_road_err_locked',
  // супер-овер дня
  // мир v3: окно «Команда»
  not_enough_gold: 'tg_team_err_gold',
  battle_hero_locked: 'tg_team_err_locked',
  // этап 2: потолок редкости, сумка с вещами
  battle_hero_capped: 'tg_team_err_capped',
  battle_hero_not_hired: 'tg_kit_err_not_hired',
  battle_bag_full: 'tg_kit_err_full',
  task_set_not_ready: 'tg_tasks_err_set',
  store_good_unavailable: 'tg_store_err_good',
  // HQ-977: покровители — уже куплен, снят с продажи, раздел закрыт правилом (reason patrons_closed)
  patron_owned: 'tg_patron_err_owned',
  patron_off_sale: 'tg_patron_err_off_sale',
  patrons_closed: 'tg_patron_err_closed',
  battle_star_set_incomplete: 'tg_star_err_incomplete',
  // §300: ранги, сердца войны, усиление реликвий
  battle_rank_max: 'tg_rank_err_max',
  not_enough_expert: 'tg_err_expert',
  battle_relic_locked: 'tg_relic_err_locked',
  battle_merge_not_enough: 'tg_kit_err_merge',
  // HQ-918 Ш-3: переработка осколков особых героев (коды ответа elite/recycle/)
  elite_recycle_not_enough: 'tg_elite_err_not_enough',
  elite_not_owned: 'tg_elite_err_not_owned',
  elite_recycle_invalid: 'tg_elite_err_invalid',
  elite_hero_not_found: 'tg_elite_err_not_found',
  // этап 3: навыки, реликвии, «Новый сезон»
  battle_skill_locked: 'tg_skill_err_locked',
  battle_skill_cooldown: 'tg_skill_err_cooldown',
  not_enough_cups: 'tg_relic_err_cups',
  battle_relic_max: 'tg_relic_err_max',
  battle_relic_special: 'tg_relic_err_special',
  battle_relics_empty: 'tg_relic_err_empty',
  battle_season_locked: 'tg_season_err_locked',
  battle_nothing_pending: 'tg_season_err_nothing_pending',
  // этап 4: «Капитан дня», лагерь
  battle_daily_fighting: 'tg_daily_err_fighting',
  battle_daily_no_player: 'tg_daily_err_no_player',
  battle_daily_not_started: 'tg_daily_err_not_started',
  battle_camp_busy: 'tg_camp_err_busy',
  battle_camp_full: 'tg_camp_err_full',
  battle_camp_not_back: 'tg_camp_err_not_back',
  // этап 5: недельное событие и босс гильдии
  battle_event_nothing: 'tg_event_err_nothing',
  battle_event_invalid: 'tg_event_err_nothing',
  guild_boss_no_guild: 'tg_gboss_err_no_guild',
  guild_boss_fighting: 'tg_daily_err_fighting',
  guild_boss_no_attempts: 'tg_gboss_err_no_attempts',
  guild_boss_not_started: 'tg_daily_err_not_started',
  guild_boss_nothing: 'tg_event_err_nothing',
  // друзья (friend_not_found — 404)
  gift_none: 'tg_friends_err_none',
  gift_limit_reached: 'tg_friends_err_limit',
  friend_not_found: 'tg_friends_err_not_friend',
  referral_code_invalid: 'tg_friends_err_code',
  referral_self: 'tg_friends_err_self',
  referral_already: 'tg_friends_err_already',
  referral_too_late: 'tg_friends_err_late',
  // самоцветы (и наборы магазина за них)
  not_enough_gems: 'tg_gems_err_not_enough',
  // §289 витрина
  store_champion_bought: 'tg_store_err_champion_bought',
  store_item_unavailable: 'tg_store_err_unavailable',
  stars_closed: 'tg_store_stars_closed',
  stars_unavailable: 'tg_store_stars_unavailable',
  // §287 ставка на серию
  streak_bet_active: 'tg_streak_err_active',
  streak_bet_closed: 'tg_streak_err_closed',
  gems_milestone_locked: 'tg_gems_err_locked',
  gems_already_claimed: 'tg_gems_err_claimed',
  energy_full: 'tg_gems_err_energy_full',
  // §281 «Карта валют»: медали, редкость, возврат из лагеря, сброс навыка
  not_enough_medals: 'tg_err_medals',
  battle_rarity_max: 'tg_rarity_err_max',
  battle_camp_back: 'tg_camp_err_back',
  battle_skill_active: 'tg_skill_err_active',
  battle_skill_ready: 'tg_skill_err_ready',
  // §302–§303: VIP, контракт легенды, подступень легендарного только за самоцветы
  vip_max: 'tg_vip_err_max',
  contract_bought: 'tg_contract_err_bought',
  player_not_in_roster: 'tg_contract_err_player',
  battle_currency_invalid: 'tg_err_currency',
  // товар снят правилом / следующий уровень VIP уже не за Stars — витрина устарела, её перечитают
  contract_closed: 'tg_store_err_unavailable',
  vip_not_for_stars: 'tg_store_err_unavailable',
  // §317: кнопки рекламы экрана боя
  ads_closed: 'tg_ad_unavailable',
  ads_limit: 'tg_ads_limit',
  ads_nothing_to_reset: 'tg_ads_nothing_to_reset',
  // §376 (HQ-786, HQ-790): раздел закрыт до уровня боя (level в ответе); своей подписи в 409 сервер не шлёт
  locked_level: 'tg_world_err_locked_level',
};

/** Текст сбоя действия: ветвимся только по code и машинной reason (detail не локализован). */
export function actionErrorKey(error: unknown): StringKey {
  if (error instanceof ApiError && error.code === 'game_action_unavailable') {
    const reason = error.payload?.reason;
    return (typeof reason === 'string' && REASONS[reason]) || 'tg_world_err_unavailable';
  }
  // 404 витрины приходит своим кодом (player_not_in_roster), не причиной
  if (error instanceof ApiError && REASONS[error.code ?? '']) return REASONS[error.code ?? '']!;
  // §291: страна не из списка
  if (error instanceof ApiError && error.code === 'country_invalid') return 'tg_world_err_country_invalid';
  return 'tg_world_err_generic';
}

/** HQ-952 (О-2): группы выбора клуба — своей страны первыми, ниже «Другие клубы». Ни у одной строки нет group (старый
 *  ответ) — один список без заголовка; строка без группы или с незнакомой — к прочим (команду не теряем). Порядок
 *  внутри группы — сервера. Пустых групп нет. */
export type TeamChoiceGroup<T> = { group: 'country' | 'other' | null; items: T[] };
export function teamChoiceGroups<T extends Pick<WorldTeamChoice, 'group'>>(choices: T[]): Array<TeamChoiceGroup<T>> {
  if (!choices.some((c) => c.group === 'country' || c.group === 'other')) return choices.length ? [{ group: null, items: choices }] : [];
  const country = choices.filter((c) => c.group === 'country');
  const other = choices.filter((c) => c.group !== 'country');
  return [{ group: 'country' as const, items: country }, { group: 'other' as const, items: other }].filter((g) => g.items.length > 0);
}

/** Подпись группы выбора клуба (ключ строки и подстановка); у списка без групп — нет. Страна известна (my_team.country)
 *  — «Clubs of <name>», нет — «Clubs of your country». */
export function teamChoiceGroupTitle(group: TeamChoiceGroup<unknown>['group'], countryName?: string | null): [StringKey, ...string[]] | null {
  if (group === 'country') return countryName ? ['tg_club_group_country_of', countryName] : ['tg_club_group_country'];
  return group === 'other' ? ['tg_club_group_other'] : null;
}

/** Блоки «Рейтинга мира» (этап 3а). Незнакомый — мимо. */
export const RATING_BLOCK_KEYS = ['division', 'season', 'leaderboards', 'nation', 'clubs'];

export function visibleRatingBlocks(order: string[], data: Record<string, unknown>): string[] {
  return order.filter((key) => RATING_BLOCK_KEYS.includes(key) && data[key] != null);
}

/** Раздел rating ведёт на прежнюю таблицу лидеров (старый ответ сервера) — рисуем её, а не «Рейтинг мира». */
export const isLeaderboardEndpoint = (endpoint: string) => /\/leaderboard\/?$/.test(endpoint.split('?')[0] ?? '');

/** Что рисовать в дивизионе: группа (active, results_pending — прошлая неделя) или только подсказка. */
export function divisionView(d: WorldDivision): { group: WorldDivisionGroup | null; deadline: string | null; pending: boolean } {
  const group = (d.state === 'active' || d.state === 'results_pending') ? d.group : null;
  const pending = d.state === 'results_pending';
  return { group, pending, deadline: group ? (pending ? group.results_at : group.ends_at) : null };
}

/** Блоки экрана гильдий (этап 3б). Незнакомый — мимо. */
export const GUILD_BLOCK_KEYS = ['my_guild', 'open_guilds', 'leagues'];
export function visibleGuildBlocks(order: string[], data: Record<string, unknown>): string[] {
  return order.filter((key) => GUILD_BLOCK_KEYS.includes(key) && data[key] != null);
}

/** Раздел guild ведёт на новый экран гильдий (world/guilds/) — иначе прежняя вкладка лиг. */
/** Экран вкладки мира по адресу: world/matches/, world/club/ (навигация v3, §273). */
export const isWorldTabEndpoint = (endpoint: string, key: string) => new RegExp(`/world/${key}/?$`).test(endpoint.split('?')[0] ?? '');

export const isGuildsEndpoint = (endpoint: string) => /\/world\/guilds\/?$/.test(endpoint.split('?')[0] ?? '');

/** §306: клуб команды (болельщики «моей команды»), не ручная гильдия и не лига друзей. Признак один — team_id строки лиги
 *  (league_row сервера: и в world/guilds/, и в leagues/, и в экране лиги). Вождя, помощников, заявок, кода, группы Telegram
 *  и выхода у клуба нет: членство следует за командой. */
export const isTeamClub = (row: Pick<FriendLeagueRowLike, 'team_id'>) => row.team_id != null;

/** Кнопки сцены строго по buttons_order; открыть нечем (незнакомый opens, нет блока в ответе) — кнопки нет.
 *  HQ-954: закрытая (locked) — есть всегда: замок с подписью виден и без адреса. */
export function sceneButtons(scene: WorldScene, world: Record<string, unknown>): WorldSceneButton[] {
  const byKey = new Map(scene.buttons.map((b) => [b.key, b]));
  return scene.buttons_order.flatMap((key) => {
    const b = byKey.get(key);
    return b && (lockOf(b.locked) || opensTarget(b.opens, world)) ? [b] : [];
  });
}

/** HQ-954: замок раздела из ответа {level, text, current?}; не той формы (нет подписи, уровня) — открыт, null.
 *  HQ-790: current сервер шлёт только с правилом unlock_progress — без него замок без полоски (current не выдумываем). */
export function lockOf(v: unknown): WorldLevelLock | null {
  if (!v || typeof v !== 'object') return null;
  const l = v as Partial<WorldLevelLock>;
  if (!(typeof l.text === 'string' && l.text && typeof l.level === 'number')) return null;
  return { level: l.level, ...(typeof l.current === 'number' && Number.isFinite(l.current) ? { current: l.current } : {}), text: l.text };
}

/** Доля полоски замка: current из level, в пределах 0…1 (level 0, нет current и мусор — пустая). */
export function lockShare(lock: WorldLevelLock): number {
  if (!(lock.level > 0) || typeof lock.current !== 'number' || !Number.isFinite(lock.current)) return 0;
  return Math.min(1, Math.max(0, lock.current / lock.level));
}

/** HQ-954: ключ руки-подсказки; нет, пустой или раздел закрыт — null (на закрытом руки нет). */
export function hintOf(hint: WorldHint | null | undefined, locked: unknown): string | null {
  return hint && typeof hint.key === 'string' && hint.key && !lockOf(locked) ? hint.key : null;
}

/** Экраны мира по адресу ручки (последний сегмент world/<key>/): кнопка сцены, вкладка папки, «Играть» (map). */
// world/map/ (карта калиток) и world/superover/ сняты 12.10 вместе с мини-играми (слово владельца; бэкенд 159)
// matches, club — 12.10 (слово владельца: мир без нижней панели): «Матчи» и «Клуб» открываются по адресу, как прочие окна
/** Форма (shop), альбом, «Сборная недели» и карточки сняты (В1–В3, HQ-782): их адреса — мимо, subScreenOf → null. */
/** chat — экран чата (HQ-916), открывается полосой чата сцены по chat_strip.path. */
export const SUB_SCREENS = ['login', 'gems', 'friends', 'road', 'matches', 'club', 'team', 'battlemap', 'battleprofile', 'battleitems', 'battlerelics', 'battledaily', 'battlecamp', 'battleachievements', 'battleevent', 'battleguildboss', 'guilds', 'week', 'store', 'chat'] as const;
export type SubScreen = (typeof SUB_SCREENS)[number];

export const isSubScreen = (key: string): key is SubScreen => (SUB_SCREENS as readonly string[]).includes(key);

/** HQ-953: «Режимы» выдвижной панелью — строка «Дальше» и кошелёк из ответа как есть. */
export interface HubPanelView { next: WorldPanelNext | null; wallet: WorldPanelWallet[] }

/** Вкладка папки, которую умеем открыть (или закрытая — locked, тогда target может быть { kind: 'locked' }); hint — ключ руки. */
export interface HubTabView { key: string; icon: string; title: string; badge: WorldDot; target: OpenTarget; locked?: WorldLevelLock; hint?: string }

export type OpenTarget = { kind: 'block'; block: string } | { kind: 'blocks'; blocks: string[] } | { kind: 'tab'; tab: string; topTab?: string }
  | { kind: 'leagues'; endpoint: string } | { kind: SubScreen; endpoint: string; topTab?: string }
  | { kind: 'hub'; hub: string; default: string; tabs: HubTabView[]; grid?: boolean; panel?: HubPanelView }
  /** §273-B: раскрыть кнопки навыков экрана боя (battle.skills), ручки нет */
  | { kind: 'skills' }
  /** 12.10: «Моя команда» — шторка выбора команды (вкладка «Режимов»); endpoint — экран «Клуба» (кланы; битва фанатов снята §306). */
  | { kind: 'myteam'; endpoint: string }
  /** HQ-954: закрытый раздел без адреса — открыть нечего, тап только показывает подпись замка. */
  | { kind: 'locked' };

/** Вкладка папки или плитка панели: открыть нечем и не закрыта — мимо; закрыта — есть всегда (с замком). */
function hubTabView(key: string, tab: WorldHubTab, world: Record<string, unknown>): HubTabView[] {
  const locked = lockOf(tab.locked);
  const target = opensTarget(tab.opens, world, true) ?? (locked ? { kind: 'locked' as const } : null);
  if (!target) return [];
  const hint = hintOf(tab.hint, tab.locked);
  return [{ key, icon: tab.icon, title: tab.title, badge: tab.badge, target, ...(locked ? { locked } : {}), ...(hint ? { hint } : {}) }];
}

const pathOf = (endpoint: string) => endpoint.split('?')[0]!;

/** Адрес игрового мира: game/<спорт>/world/ (§288: вкладка fantasy Mini App ведёт сюда, без мира — на экран игры). */
export const isWorldPath = (url: string) => /\/game\/[a-z_]+\/world\/?$/.test(pathOf(url));

/** Экран мира по адресу ручки: world/road/ → road; незнакомый — null. */
export function subScreenOf(endpoint: string): SubScreen | null {
  // мир v3: окна боя — world/battle/team|map|profile|items|relics|daily|camp/ (сам бой — на сцене, не экраном);
  // старый боевой календарь world/battle/calendar/ снят (§278/§284, HQ-780) — мимо, null
  const b = /\/world\/battle\/(team|map|profile|items|relics|daily|camp|achievements|event|guild-boss)\/?$/.exec(pathOf(endpoint));
  if (b) return ({ team: 'team', map: 'battlemap', profile: 'battleprofile', items: 'battleitems', relics: 'battlerelics', daily: 'battledaily', camp: 'battlecamp', achievements: 'battleachievements', event: 'battleevent', 'guild-boss': 'battleguildboss' } as const)[b[1] as 'team'];
  const m = /\/world\/([a-z_]+)\/?$/.exec(pathOf(endpoint));
  return m && (SUB_SCREENS as readonly string[]).includes(m[1]!) ? (m[1] as SubScreen) : null;
}

/** Что открыть: блок(и) этого ответа (умеем рисовать и приехали), раздел мира, экран лиг друзей (leagues/), экран мира
 *  по адресу, папка сцены (вкладки — то же, без вложенных папок; открыть нечем ни одну — папки нет). */
export function opensTarget(opens: WorldOpens | null | undefined, world: Record<string, unknown>, nested = false): OpenTarget | null {
  if (!opens) return null;
  const known = (b: string) => WORLD_BLOCK_KEYS.includes(b) && world[b] != null;
  if (opens.kind === 'block' && opens.block && known(opens.block)) return { kind: 'block', block: opens.block };
  if (opens.kind === 'blocks' && opens.blocks) {
    const blocks = opens.blocks.filter(known);
    return blocks.length ? { kind: 'blocks', blocks } : null;
  }
  if (opens.kind === 'tab' && opens.tab) return opens.top_tab ? { kind: 'tab', tab: opens.tab, topTab: opens.top_tab } : { kind: 'tab', tab: opens.tab };
  if (opens.kind === 'skills' && !nested) return { kind: 'skills' };
  if (opens.kind === 'my_team' && opens.endpoint) return { kind: 'myteam', endpoint: opens.endpoint };
  if (opens.kind === 'endpoint' && opens.endpoint) {
    if (/\/leagues\/?$/.test(pathOf(opens.endpoint))) return { kind: 'leagues', endpoint: opens.endpoint };
    const sub = subScreenOf(opens.endpoint);
    // top_tab рядом с адресом — экран откроется на этой вкладке сверху («Клуб» → дивизион / гильдия)
    return sub ? { kind: sub, endpoint: opens.endpoint, ...(opens.top_tab ? { topTab: opens.top_tab } : {}) } : null;
  }
  // HQ-953: layout panel — плитки списком tiles (их может быть 0: панель всё равно с кошельком и «Дальше»)
  if (opens.kind === 'hub' && opens.hub && opens.layout === 'panel' && Array.isArray(opens.tiles) && !nested) {
    const tabs = opens.tiles.flatMap((tile) => hubTabView(tile.key, tile, world));
    const def = openDefault(tabs, opens.default)?.key ?? '';
    return { kind: 'hub', hub: opens.hub, default: def, tabs, panel: { next: opens.next ?? null, wallet: opens.wallet ?? [] } };
  }
  if (opens.kind === 'hub' && opens.hub && opens.tabs && !nested) {
    const tabs = (opens.tabs_order ?? []).flatMap((key) => {
      const tab = opens.tabs![key];
      return tab ? hubTabView(key, tab, world) : [];
    });
    if (!tabs.length) return null;
    const def = (openDefault(tabs, opens.default) ?? tabs[0]!).key;
    return { kind: 'hub', hub: opens.hub, default: def, tabs, ...(opens.layout === 'grid' ? { grid: true } : {}) };
  }
  return null;
}

/** Вкладка по умолчанию — default сервера, если она открыта, иначе первая открытая (закрытую не выбираем). */
export function openDefault<T extends { key: string; locked?: WorldLevelLock }>(tabs: T[], wanted: string | undefined): T | undefined {
  return tabs.find((x) => x.key === wanted && !x.locked) ?? tabs.find((x) => !x.locked);
}

/** Вкладки сверху экрана (рейтинг, сообщество): блоки этого ответа, которые умеем рисовать, или ручка, которую умеем
 *  открыть (resolve). Ни одной — вкладок нет (рисуем прежнюю ленту по blocks_order). */
export type TopTabTarget = { kind: 'blocks'; blocks: string[] } | { kind: 'leaderboard'; endpoint: string } | { kind: 'friends'; endpoint: string }
  /** навигация v3 (§273): «Матчи» — прогнозы (fixtures/) и экраны мира по адресу */
  | { kind: 'predictions'; endpoint: string } | { kind: 'sub'; sub: SubScreen; endpoint: string };

/** Список матчей для прогнозов — game/<sport>/fixtures/. */
export const isFixturesEndpoint = (endpoint: string) => /\/game\/[a-z_]+\/fixtures\/?$/.test(endpoint.split('?')[0] ?? '');
export interface TopTabView { key: string; title: string; badge: WorldDot; target: TopTabTarget }

export function topTabs(top: WorldTopTabs | null | undefined, known: (block: string) => boolean): { tabs: TopTabView[]; default: string } | null {
  if (!top) return null;
  const tabs = top.tabs_order.flatMap((key): TopTabView[] => {
    const tab = top.tabs[key];
    const o = tab?.opens;
    if (!tab || !o) return [];
    let target: TopTabTarget | null = null;
    if (o.kind === 'blocks' && o.blocks) {
      const blocks = o.blocks.filter(known);
      if (blocks.length) target = { kind: 'blocks', blocks };
    } else if (o.kind === 'endpoint' && o.endpoint) {
      if (isLeaderboardEndpoint(o.endpoint)) target = { kind: 'leaderboard', endpoint: o.endpoint };
      else if (isFixturesEndpoint(o.endpoint)) target = { kind: 'predictions', endpoint: o.endpoint };
      else {
        const sub = subScreenOf(o.endpoint);
        if (sub === 'friends') target = { kind: 'friends', endpoint: o.endpoint };
        else if (sub) target = { kind: 'sub', sub, endpoint: o.endpoint };
      }
    }
    return target ? [{ key, title: tab.title, badge: tab.badge, target }] : [];
  });
  if (!tabs.length) return null;
  return { tabs, default: tabs.find((x) => x.key === top.default)?.key ?? tabs[0]!.key };
}

/** Строка плашки: дивизион — «5 · Silver» (место есть) или подсказка; гильдия — счёт войны или подсказка. */
export function bannerLine(b: WorldBanner): string {
  if (b.key === 'guild') {
    if (b.us_score != null && b.them_score != null) return `${b.us_score} : ${b.them_score}`;
    return b.hint ?? '';
  }
  if (b.place != null) return [b.size ? `${b.place}/${b.size}` : String(b.place), b.tier_title].filter(Boolean).join(' · ');
  return b.hint ?? b.tier_title ?? '';
}

/** Адрес экрана кнопки сцены (команда, карта), как его откроет сама кнопка; нет кнопки или другой экран — null. */
export function sceneScreenPath(world: { scene?: { buttons: Array<{ key: string; opens: WorldOpens | null }> } } | undefined, key: string, kind: string): string | null {
  const b = world?.scene?.buttons.find((x) => x.key === key);
  const target = world && b ? opensTarget(b.opens, world as unknown as Record<string, unknown>) : null;
  if (target && target.kind === kind && 'endpoint' in target) return target.endpoint;
  // кнопка открывает папку (§273-B: «Команда» — вкладки team/relics/…, «Магазин» — store/gems/…) — экран ищем во вкладках
  const tab = target?.kind === 'hub' ? target.tabs.find((x) => x.target.kind === kind && 'endpoint' in x.target) : undefined;
  return tab && 'endpoint' in tab.target ? tab.target.endpoint : null;
}

/** Что положить в кэш под заставкой (слово владельца 12.10): бой, экраны кнопок сцены и вкладок их папок,
 *  разделы нижней панели мира — всё, куда человек попадает с главной одним нажатием. Без повторов; порядок — очередь загрузки. */
// чат (HQ-916) не предзагружаем: лента живая (опрос с after), её кэш — свой (chatQueries)
export type PreloadTarget = { kind: Exclude<SubScreen, 'chat'> | 'battle' | 'rating' | 'fixtures'; path: string };
export function preloadTargets(world: {
  scene?: { battle?: { endpoint: string } | null; buttons: Array<{ key: string; opens: WorldOpens | null }> };
  tab_bar?: WorldTabBar;
}): PreloadTarget[] {
  const out: PreloadTarget[] = [];
  const add = (kind: PreloadTarget['kind'], path: string | null | undefined) => {
    if (path && !out.some((x) => x.kind === kind && x.path === path)) out.push({ kind, path });
  };
  const fromTarget = (t: OpenTarget | null) => {
    if (t?.kind === 'myteam') add('club', t.endpoint);
    else if (t && 'endpoint' in t && isSubScreen(t.kind) && t.kind !== 'chat') add(t.kind, t.endpoint);
  };
  add('battle', world.scene?.battle?.endpoint);
  // разделы панели — сразу за боем: у них вторая волна (прогнозы «Матчей»), её ждать дольше всего
  for (const tab of visibleTabs(world.tab_bar)) {
    if (tab.key === 'matches' && isWorldTabEndpoint(tab.endpoint, 'matches')) add('matches', tab.endpoint);
    if (tab.key === 'club' && isWorldTabEndpoint(tab.endpoint, 'club')) add('club', tab.endpoint);
    if (tab.key === 'rating' && !isLeaderboardEndpoint(tab.endpoint)) add('rating', tab.endpoint);
    if (tab.key === 'guild' && isGuildsEndpoint(tab.endpoint)) add('guilds', tab.endpoint);
  }
  const asRecord = world as unknown as Record<string, unknown>;
  for (const b of world.scene?.buttons ?? []) {
    const target = opensTarget(b.opens, asRecord);
    fromTarget(target);
    if (target?.kind === 'hub') for (const tab of target.tabs) fromTarget(tab.target);
  }
  return out;
}

/** Вторая волна: вкладки сверху у загруженного экрана («Матчи» — прогнозы; «Клуб» и гильдии —
 *  друзья). main — вкладка по умолчанию (её видно сразу при открытии экрана): заставка ждёт только её, остальные
 *  догружаются после. Таблицы лидеров не грузим: постраничные, период известен лишь из ответа — откроются сами. */
export function nestedTargets(screen: unknown): Array<PreloadTarget & { main: boolean }> {
  const top = (screen as { top_tabs?: WorldTopTabs | null } | null | undefined)?.top_tabs;
  if (!top) return [];
  return top.tabs_order.flatMap((key): Array<PreloadTarget & { main: boolean }> => {
    const o = top.tabs[key]?.opens;
    if (o?.kind !== 'endpoint' || !o.endpoint || isLeaderboardEndpoint(o.endpoint)) return [];
    const main = key === (top.default ?? top.tabs_order[0]);
    if (isFixturesEndpoint(o.endpoint)) return [{ kind: 'fixtures', path: o.endpoint, main }];
    const sub = subScreenOf(o.endpoint);
    return sub && sub !== 'chat' ? [{ kind: sub, path: o.endpoint, main }] : [];
  });
}
